import React from 'react';
// import { useRankedScores } from './useRankedScores'; // <-- Adjust path if needed

// --- MUI Components ---
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh'; // Optional icon
import LinearProgress from '@mui/material/LinearProgress';
import { useUserList } from '../hooks';
import { useNavigate } from 'react-router-dom';
import UserHeader from "../Components/User/UserHeader.component"; // Assuming this component exists

function LeaderboardPage() {
    const navigate = useNavigate();
    const [sortBy, setSortBy] = React.useState('trader'); // Default sort by 'trader'
    const [limit, setLimit] = React.useState(10); // Default limit
    // Use the custom hook - initial sort by 'trader', 10 items per page
    const { data, loading, error, page: currentPage, setPage } = useUserList(limit, sortBy);
    const scores = data?.data || []; // Fallback to empty array if undefined
    const {
        metadata,

    } = data;


    // --- Event Handlers ---
    const refresh = () => {
        setPage(1); // Reset to page 1 on refresh
        setLimit(10); // Reset limit to default on refresh
        setSortBy('trader'); // Reset sort to default on refresh
    };

    // Handler for MUI TablePagination's page change (0-based index)
    const handlePageChange = (event, newPage) => {
        setPage(newPage + 1); // Convert 0-based index back to 1-based for our hook
    };

    // Handler for MUI TablePagination's rows per page change
    const handleRowsPerPageChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        // setPage(1); // setLimit already resets the page in our hook
    };

    // Handler for the sorting dropdown
    const handleSortChange = (event) => {
        setSortBy(event.target.value);
    };

    // --- Rendering Logic ---

    const renderContent = () => {
        // Initial Loading State (before any data/error)
        if (loading && !scores.length && !error) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </Box>
            );
        }

        // Error State
        if (error) {
            return (
                <Alert severity="error" action={
                    <Button color="inherit" size="small" onClick={refresh}>
                        Retry
                    </Button>
                }>
                    Error loading leaderboard: {error}
                </Alert>
            );
        }


        // Data Loaded State (or empty state)
        return (
            <Paper sx={{ width: '100%', mb: 2 }}>
                {/* Optional: Show linear progress during refresh */}
                {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, width: '100%' }} />}
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', background: "none" }}>Rank</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', background: "none" }}>User ID</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', background: "none" }}>
                                Trader Score {sortBy === 'trader' ? '▼' : ''}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', background: "none" }}>
                                Creator Score {sortBy === 'creator' ? '▼' : ''}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', background: "none" }}>Last Updated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scores.length === 0 && !loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No users found matching the criteria.
                                </TableCell>
                            </TableRow>
                        ) : (
                            scores.map((user) => (
                                <TableRow
                                    hover
                                    key={user.userId}
                                    sx={{
                                        '&:last-child td, &:last-child th': { border: 0 },
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        },
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => window.open(`/users/${ user.userId }`)} // Navigate to user page on click
                                >
                                    <TableCell component="th" scope="row">
                                        {user.rank}
                                    </TableCell>
                                    <TableCell>
                                        <UserHeader userId={user.userId} usePnlButton={false} />

                                    </TableCell>
                                    <TableCell align="right">{user.traderScore?.toFixed(4) ?? 'N/A'}</TableCell>
                                    <TableCell align="right">{user.creatorScore?.toFixed(4) ?? 'N/A'}</TableCell>
                                    <TableCell align='right'>
                                        {user.scoresLastUpdated
                                            ? new Date(user.scoresLastUpdated).toLocaleString()
                                            : 'N/A'}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {/* Pagination - Only show if metadata is available */}
                {metadata && metadata.totalMatchingUsers > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 20, 50]} // Options for rows per page
                        component="div" // Important for placement
                        count={metadata.totalMatchingUsers} // Total number of items
                        rowsPerPage={limit} // Current rows per page
                        page={currentPage - 1} // Current page (0-based index required by component)
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        // Show loading state on pagination controls as well
                        nextIconButtonProps={{ disabled: loading }}
                        backIconButtonProps={{ disabled: loading }}
                        SelectProps={{ disabled: loading }}
                    />
                )}
            </Paper>
        );
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{
                "&:first-letter": {
                    textTransform: "uppercase",
                },
                fontWeight: 'bold',
            }}>
                {sortBy} Leaderboard
            </Typography>

            {/* Controls Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <FormControl sx={{ minWidth: 150 }} size="small">
                    <InputLabel id="sort-by-label">Sort By</InputLabel>
                    <Select
                        labelId="sort-by-label"
                        id="sort-by-select"
                        value={sortBy}
                        label="Sort By"
                        onChange={handleSortChange}
                        disabled={loading} // Disable while loading
                    >
                        <MenuItem value={'trader'}>Trader Score</MenuItem>
                        <MenuItem value={'creator'}>Creator Score</MenuItem>
                    </Select>
                </FormControl>

                {metadata && (
                    <Typography variant="caption">
                        Showing ranks {scores.length > 0 ? scores[0].rank : 0} - {scores.length > 0 ? scores[scores.length - 1].rank : 0} of {metadata.totalMatchingUsers}
                    </Typography>
                )}

                <Button
                    variant="outlined"
                    onClick={refresh}
                    disabled={loading} // Disable while loading
                    startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                >
                    Refresh
                </Button>
            </Box>

            {/* Content: Loading, Error, or Table */}
            {renderContent()}

        </Container>
    );
}

export default LeaderboardPage;